import React from 'react';
import { Pagination, Button, Form, Input, Spin } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import CustomModal from '../../../components/common/CustomModal';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getContent, addContent } from '../../../store/slices/adminSlice';
import { useTableState, useModal, useDataLoading } from '../../../hooks';
import { requiredRule } from '../../../utils';

const { TextArea } = Input;

// TypeScript interfaces
interface ContentData {
  _id: string;
  title: string;
  content: string;
  contentType?: string;
}

interface ContentFormValues {
  title: string;
  content: string;
}

const Content: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<ContentFormValues>();

  // Redux state
  const contentData = useAppSelector((state) => state.admin.contentData) as ContentData[];
  const totalCount = useAppSelector((state) => state.admin.contentCount);

  // Use custom hooks for common patterns
  const { currentPage, sortOrder, changePage, sortData } = useTableState({
    fetchAction: getContent,
  });

  const { visible, itemId: contentId, showModal, hideModal } = useModal({ form });

  const [loading] = useDataLoading(contentData);

  const handleShowModal = (id: string, obj?: ContentData) => {
    showModal(id);
    if (id !== '' && obj) {
      form.setFieldsValue({ content: obj.content, title: obj.title });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const obj = {
        title: values.title,
        content: values.content,
        contentId: contentId,
      };
      hideModal();
      dispatch(addContent(obj));
    } catch {
      // Validation failed - form will show errors
    }
  };

  // Render content rows
  const contentRows = contentData.map((x) => (
    <tr key={x._id}>
      <td>{x.title}</td>
      <td>{x.content}</td>
      <td className="action-icons">
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => handleShowModal(x._id, x)}
        >
          <i className="fa fa-pencil" style={{ color: '#666' }}></i>
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader prefixedClassName="ant-page-header" title="Rules" subTitle="" />
        <Spin spinning={loading} delay={500}>
          <div className="box-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <SortableTableHeader
                      label="Title"
                      sortKey="title"
                      currentSortOrder={sortOrder}
                      onSort={sortData}
                    />
                    <SortableTableHeader
                      label="Rules"
                      sortKey="content"
                      currentSortOrder={sortOrder}
                      onSort={sortData}
                    />
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>{contentRows}</tbody>
              </table>
            </div>
            <Pagination
              style={{ textAlign: 'right' }}
              pageSize={5}
              defaultCurrent={currentPage}
              total={totalCount ? totalCount : 1}
              onChange={changePage}
            />
            <CustomModal
              handleCancel={hideModal}
              handleOk={handleOk}
              visible={visible}
              title=""
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={form} className="sport-form l_hgt0" layout="vertical">
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[requiredRule('title')]}
                >
                  <Input placeholder="Title" />
                </Form.Item>
                <Form.Item
                  label="Content"
                  name="content"
                  rules={[requiredRule('content')]}
                >
                  <TextArea placeholder="Topic" />
                </Form.Item>
              </Form>
            </CustomModal>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default Content;
